"use client";

import * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../primitives/alert-dialog";
import { cn } from "../../lib/cn";
import { useDialogStore } from "./useDialog";

/**
 * AndoDialogProvider — renderer for the imperative `confirm/alert/prompt`
 * API. Mount once at the app root; the store-driven queue takes care of
 * showing one dialog at a time.
 *
 * Why this exists: ``window.confirm`` and friends are visually grim and
 * sit outside the brand. This provider routes every confirmation through
 * a single shadcn-style AlertDialog with the Ando warm chrome (cream
 * surface, hairline border, ink/orange typography). Promise-based: any
 * caller `await`s the dialog and gets a boolean / string / void back.
 */

export function AndoDialogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { i18n } = useLingui();
  const active = useDialogStore((s) => s.active);
  const resolve = useDialogStore((s) => s.resolve);

  // Local input state for `prompt` dialogs. We reset whenever the active
  // dialog identity changes so old values don't bleed across opens.
  const [inputValue, setInputValue] = React.useState("");
  React.useEffect(() => {
    if (active?.config.kind === "prompt") {
      setInputValue(active.config.initialValue ?? "");
    }
  }, [active?.id, active?.config]);

  if (!active) {
    return <>{children}</>;
  }

  const cfg = active.config;
  const isPrompt = cfg.kind === "prompt";
  const isAlert = cfg.kind === "alert";
  const destructive = cfg.destructive === true;
  const confirmLabel =
    cfg.confirmLabel ??
    (cfg.kind === "confirm" ? i18n._(msg`Conferma`) : i18n._(msg`OK`));
  const cancelLabel = cfg.cancelLabel ?? i18n._(msg`Annulla`);

  const promptMinLength = isPrompt ? (cfg.minLength ?? 1) : 0;
  const promptValid = !isPrompt || inputValue.trim().length >= promptMinLength;

  const onConfirm = (): void => {
    if (isPrompt) {
      if (!promptValid) return;
      resolve(inputValue);
    } else if (isAlert) {
      resolve(true);
    } else {
      resolve(true);
    }
  };
  const onCancel = (): void => {
    resolve(isAlert ? true : isPrompt ? null : false);
  };

  return (
    <>
      {children}
      <AlertDialog open onOpenChange={(open) => !open && onCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{cfg.title}</AlertDialogTitle>
            {cfg.description ? (
              <AlertDialogDescription>{cfg.description}</AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>

          {isPrompt ? (
            <div className="flex flex-col gap-1.5">
              {cfg.label ? (
                <label
                  htmlFor="ando-dialog-prompt-input"
                  className="font-medium text-[11px] text-ink-3 uppercase tracking-[0.08em]"
                >
                  {cfg.label}
                </label>
              ) : null}
              <input
                id="ando-dialog-prompt-input"
                type={cfg.inputType ?? "text"}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && promptValid) {
                    e.preventDefault();
                    onConfirm();
                  }
                }}
                placeholder={cfg.placeholder}
                // biome-ignore lint/a11y/noAutofocus: dialog focus restoration handled by Radix
                autoFocus
                className={cn(
                  "rounded-md border border-hairline bg-white/55 px-3 py-2 text-[13px] text-ink-1",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
                )}
              />
            </div>
          ) : null}

          <AlertDialogFooter>
            {!isAlert ? (
              <AlertDialogCancel onClick={onCancel}>
                {cancelLabel}
              </AlertDialogCancel>
            ) : null}
            <AlertDialogAction
              onClick={onConfirm}
              disabled={isPrompt && !promptValid}
              className={cn(
                destructive &&
                  "bg-rose-600 text-white hover:bg-rose-600/90 focus-visible:ring-rose-500/45",
              )}
            >
              {confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
